import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { TeamApiService } from 'app/library/team/services/team.api-service';
import { TeamService } from 'app/library/team/services/team.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AddUserToTeamDialogComponent } from '../dialogs/add-user-to-team-dialog/add-user-to-team-dialog.component';

@Component({
    selector: 'app-team-training-responsibles',
    templateUrl: './team-training-responsibles.component.html',
    styleUrls: ['./team-training-responsibles.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamTrainingResponsiblesComponent extends UnsubscribeOnDestroy implements OnInit, OnChanges {
    @Input()
    teamId: string;

    @Input()
    isTeamDetailsPage: string;

    @Input()
    canManageTrainingResponsibles: boolean;

    @Input()
    allUser$: Observable<User[]>;

    @Input()
    filteredAllUsers$: Observable<User[]>;

    trainingResponsibles: User[] = [];

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    addUserToTeamDialogRef: MatDialogRef<AddUserToTeamDialogComponent>;

    constructor(
        public sideNav: SidenavService,
        public teamService: TeamService,
        private _teamApiService: TeamApiService,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar
    ) {
        super();
    }

    ngOnInit(): void {
        this.teamService.refreshTeamTrainingResponsibles$.pipe(takeUntil(this._unsubscribeAll)).subscribe((teamId) => {
            if (teamId === this.teamId) {
                void this.fetchTrainingResponsibles();
            }
        });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.teamId?.currentValue) {
            await this.fetchTrainingResponsibles();
        }
    }

    async fetchTrainingResponsibles(): Promise<void> {
        this.trainingResponsibles = (await this._teamApiService.getTeamTrainingResponsibles(this.teamId).toPromise()) || [];
    }

    // Method to open the appropriate dialog based on isTeamDetailsPage
    openAddDialog(): void {
        if (this.isTeamDetailsPage) {
            this.openAddUserToTeamDialog();
        } else {
            this.openAddTrainingResponsibleDialog();
        }
    }

    openAddTrainingResponsibleDialog(): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.allUser$;
        dialogRef.componentInstance.entityId = this.teamId;
        dialogRef.componentInstance.title = 'TEAMS.TRAINING_RESPONSIBLE.BUTTON.ADD';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'trainingResponsible';

        dialogRef.afterClosed().subscribe(async (result: { list: User[] }) => {
            if (result && result.list) {
                await this._teamApiService
                    .addTrainingResponsibles({
                        userIds: result.list.map((user) => user.id),
                        teamIds: [this.teamId],
                    })
                    .toPromise();

                await this.fetchTrainingResponsibles();
            }
        });
    }

    async openAddUserToTeamDialog(): Promise<void> {
        await this.teamService.fetchTeamLeaderTeams();

        this.addUserToTeamDialogRef = this._matDialog.open(AddUserToTeamDialogComponent, {
            disableClose: false,
            width: '900px',
            autoFocus: false,
        });

        const teams = this.teamService.getTeamLeaderTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        const teamListTree = this.teamService.convertListToTree(clonedTeams);

        this.addUserToTeamDialogRef.componentInstance.entities$ = this.filteredAllUsers$;
        this.addUserToTeamDialogRef.componentInstance.title = this._translateService.instant('TEAMS.TRAINING_RESPONSIBLE.BUTTON.ADD');
        this.addUserToTeamDialogRef.componentInstance.teamId = this.teamId;
        this.addUserToTeamDialogRef.componentInstance.role = 'trainingResponsible';
        this.addUserToTeamDialogRef.componentInstance.listTree = teamListTree;

        this.addUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedNodes: Set<User> }) => {
            if (data) {
                const userIds: string[] = Array.from(data.selectedNodes).map((user) => user.id);

                const result = await this._teamApiService
                    .addTrainingResponsibles({
                        userIds: userIds,
                        teamIds: [this.teamId],
                    })
                    .toPromise();

                await this.fetchTrainingResponsibles();

                if (result) {
                    const successMessage = this._translateService.instant('TEAMS.DIALOG.ADD_USER_TO_TEAM.TRAINING_RESPONSIBLE.MESSAGE.SUCCESS');

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
            this.addUserToTeamDialogRef = null;
        });
    }

    openRemoveTrainingResponsibleDialog(trainingResponsible: User): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.REMOVE_TRAINING_RESPONSIBLE.MESSAGE', {
            fullName: trainingResponsible.fullName,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this._teamApiService
                    .removeTrainingResponsibles({
                        userIds: [trainingResponsible.id],
                        teamId: this.teamId,
                    })
                    .toPromise();

                await this.fetchTrainingResponsibles();
            }

            this.confirmDialogRef = null;
        });
    }
}
